import * as React from 'react';

import { cn } from '../../lib/cn';

/** Props di {@link Card}: nessuna prop custom, solo gli attributi HTML standard di un div. */
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Contenitore a card generico (bordo, sfondo, hover); componi con {@link CardHeader}, {@link CardContent}, {@link CardFooter} ecc. */
const Card = React.forwardRef<HTMLDivElement, CardProps>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn(
			'rounded-xl border border-border bg-card text-card-foreground transition-all duration-200 hover:border-accent/[0.28] hover:-translate-y-px',
			className,
		)}
		{...props}
	/>
));
Card.displayName = 'Card';

/** Props di {@link CardHeader}: nessuna prop custom, solo gli attributi HTML standard di un div. */
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Intestazione di {@link Card}: contiene tipicamente {@link CardTitle} e {@link CardDescription}. */
const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
	({ className, ...props }, ref) => (
		<div ref={ref} className={cn('flex flex-col space-y-1.5 p-3.5', className)} {...props} />
	),
);
CardHeader.displayName = 'CardHeader';

/** Props di {@link CardTitle}: nessuna prop custom, solo gli attributi HTML standard di un div. */
export interface CardTitleProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Titolo di {@link Card}, va dentro {@link CardHeader}. */
const CardTitle = React.forwardRef<HTMLDivElement, CardTitleProps>(
	({ className, ...props }, ref) => (
		<div
			ref={ref}
			className={cn('text-sm font-semibold leading-none tracking-tight', className)}
			{...props}
		/>
	),
);
CardTitle.displayName = 'CardTitle';

/** Props di {@link CardDescription}: nessuna prop custom, solo gli attributi HTML standard di un div. */
export interface CardDescriptionProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Sottotitolo/descrizione di {@link Card}, va dentro {@link CardHeader}. */
const CardDescription = React.forwardRef<HTMLDivElement, CardDescriptionProps>(
	({ className, ...props }, ref) => (
		<div ref={ref} className={cn('text-[13px] text-muted-foreground', className)} {...props} />
	),
);
CardDescription.displayName = 'CardDescription';

/** Props di {@link CardContent}: nessuna prop custom, solo gli attributi HTML standard di un div. */
export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Corpo principale di {@link Card}. */
const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
	({ className, ...props }, ref) => (
		<div ref={ref} className={cn('p-3.5 pt-0', className)} {...props} />
	),
);
CardContent.displayName = 'CardContent';

/** Props di {@link CardFooter}: nessuna prop custom, solo gli attributi HTML standard di un div. */
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Piè di pagina di {@link Card} (es. azioni). */
const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
	({ className, ...props }, ref) => (
		<div ref={ref} className={cn('flex items-center p-3.5 pt-0', className)} {...props} />
	),
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
